import React, { useState, useEffect } from 'react';
import { Row, Col, Modal, ModalHeader, ModalBody, Button, FormGroup, Label, Input, Alert } from 'reactstrap';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import InputMask from 'react-input-mask';
import { priorityUkApi } from '../../services/PriorityUkService';
import { showToast } from '../../components/Toast';

const ModalPriorityUK = ({ data, wh_data, isOpen, toggle }) => {
  const [addPriorityUk] = priorityUkApi.useAddPriorityUkMutation();
  const [formData, setFormData] = useState({});
  const [formDataWh, setFormDataWh] = useState([]);
  const [checkboxState, setCheckboxState] = useState({});
  const [date, setDate] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [invoiceNo, setInvoiceNo] = useState(''); // State to hold invoiceNo value

  useEffect(() => {
    if (data) {
      setFormData(data);
      setInvoiceNo(data.invoiceNo || ''); // Initialize invoiceNo state
    }
    if (wh_data) {
      setFormDataWh(wh_data);
    }
    if (!isOpen) {
      // Reset checkbox state, date, invoice, and warning when the modal is closed
      setCheckboxState({});
      setDate('');
      setShowWarning(false);
      setInvoiceNo('');
    }
  }, [data, wh_data, isOpen]);

  const handleCheckboxChange = (managerId) => {
    setCheckboxState((prev) => ({
      ...prev,
      [managerId]: {
        ...prev[managerId],
        checked: !prev[managerId]?.checked
      }
    }));
  };

  const handleTextareaChange = (managerId, field, value) => {
    setCheckboxState((prev) => ({
      ...prev,
      [managerId]: {
        ...prev[managerId],
        [field]: value
      }
    }));
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setDate(newDate);
  };

  const handleInvoiceChange = (e) => {
    const newInvoiceNo = e.target.value;
    setInvoiceNo(newInvoiceNo);
  };

  // Update additionalDate and additionalInvoice for all managers when date or invoice changes
  useEffect(() => {
    if (date || invoiceNo) {
      setCheckboxState((prev) => {
        const updatedState = { ...prev };
        formDataWh.forEach((manager) => {
          if (!updatedState[manager._id]) {
            updatedState[manager._id] = {};
          }
          updatedState[manager._id].additionalDate = date || updatedState[manager._id].additionalDate;

          // Only set the additionalInvoice if it hasn't been manually modified (allowing independent edits)
          if (!updatedState[manager._id].hasEditedAdditionalInvoice) {
            updatedState[manager._id].additionalInvoice = invoiceNo || updatedState[manager._id].additionalInvoice;
          }
        });
        return updatedState;
      });
    }
  }, [date, invoiceNo, formDataWh]);

  const handleAdditionalInvoiceChange = (managerId, value) => {
    setCheckboxState((prev) => ({
      ...prev,
      [managerId]: {
        ...prev[managerId],
        additionalInvoice: value,
        hasEditedAdditionalInvoice: true // Mark that the additionalInvoice has been edited manually
      }
    }));
  };

  const onSubmit = (e, values) => {
    const atLeastOneChecked = Object.values(checkboxState).some((state) => state.checked);

    if (!atLeastOneChecked) {
      setShowWarning(true);
      return;
    }

    setShowWarning(false);
    const { clientName, manager, ...otherFields } = values;

    const [managerId, bitrixId] = manager.split('|');

    const newObjectsArray = [];

    Object.keys(otherFields).forEach((key) => {
      if (key.startsWith('whManager_') && otherFields[key] === true) {
        const afterUnderscore = key.split('_')[1];
        const whManagerId = afterUnderscore.split('|')[0];
        const wh_bitrixId = afterUnderscore.split('|')[1];

        const newObject = {
          invoiceNo,
          clientName,
          manager: managerId,
          bitrixID: bitrixId,
          wh_bitrixID: wh_bitrixId,
          whManager: whManagerId,
          additionalInvoice: checkboxState[whManagerId]?.additionalInvoice || '',
          additionalDate: checkboxState[whManagerId]?.additionalDate?.split('/').reverse().join('-') || '',
          address: checkboxState[whManagerId]?.address || '',
          comments: checkboxState[whManagerId]?.comments || ''
        };

        newObjectsArray.push(newObject);
      }
    });
    addPriorityUk(newObjectsArray);

    showToast('The order has been sent to Priority Ukraine');

    toggle();
  };

  return (
    <Modal size='lg' isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Send Order to Priority Ukraine</ModalHeader>
      <ModalBody>
        <AvForm onValidSubmit={onSubmit}>
          <Row>
            <Col md={3}>
              <FormGroup className='mb-3'>
                <Label for='date'>Date</Label>
                <InputMask id='date' mask='99/99/9999' value={date} onChange={handleDateChange}>
                  {(inputProps) => <AvField {...inputProps} name='date' type='text' placeholder='(DD/MM/YYYY)' />}
                </InputMask>
              </FormGroup>
            </Col>

            <Col md={2}>
              <FormGroup className='mb-3'>
                <Label for='invoiceNo'>Invoice No</Label>
                <AvField id='invoiceNo' name='invoiceNo' type='text' value={invoiceNo} onChange={handleInvoiceChange} />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className='mb-3'>
                <Label for='clientName'>Client Name</Label>
                <AvField id='clientName' name='clientName' type='text' value={formData.clientName || ''} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup className='mb-3'>
                <AvField
                  type='select'
                  name='manager'
                  id='manager'
                  className='form-select'
                  label='Manager'
                  value={formData.manager ? formData.manager._id + '|' + formData.manager.bitrixID : formData._id + '|' + formData.bitrixID}
                >
                  <option key={formData.manager && formData.manager._id} value={formData.manager && formData.manager._id + '|' + formData.manager.bitrixID}>
                    {formData.manager && formData.manager.fname} {formData.manager && formData.manager.sname}
                  </option>
                </AvField>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={12}>
              <FormGroup className='mb-3'>
                <Label>Warehouse Managers</Label>
                {formDataWh.map((manager) => (
                  <div key={manager._id} className='d-flex flex-column mb-3'>
                    <div className='d-flex align-items-center'>
                      <AvField
                        type='checkbox'
                        id={`wh-manager-${manager._id}`}
                        name={`whManager_${manager._id}|${manager.bitrixID}`}
                        value={manager._id}
                        label={`${manager.fname} ${manager.sname}`}
                        onChange={() => handleCheckboxChange(manager._id)}
                      />
                    </div>
                    {checkboxState[manager._id]?.checked && (
                      <Row className='ml-3 mt-2'>
                        <Col md={2}>
                          <Input
                            type='text'
                            placeholder='Invoice No'
                            value={checkboxState[manager._id]?.additionalInvoice || ''}
                            onChange={(e) => handleAdditionalInvoiceChange(manager._id, e.target.value)}
                            name={`additionalInvoice_${manager._id}`}
                            className='mb-2'
                          />
                        </Col>
                        <Col md={3}>
                          <InputMask mask='99/99/9999' value={checkboxState[manager._id]?.additionalDate || ''} onChange={(e) => handleTextareaChange(manager._id, 'additionalDate', e.target.value)}>
                            {(inputProps) => <Input {...inputProps} type='text' placeholder='(DD/MM/YYYY)' name={`additionalDate_${manager._id}`} className='mb-2' />}
                          </InputMask>
                        </Col>
                        <Col md={4}>
                          <Input
                            type='textarea'
                            placeholder='Address'
                            value={checkboxState[manager._id]?.address || ''}
                            onChange={(e) => handleTextareaChange(manager._id, 'address', e.target.value)}
                            name={`address_${manager._id}`}
                            className='mb-2'
                            rows={3}
                          />
                        </Col>
                        <Col md={3}>
                          <Input
                            type='textarea'
                            placeholder='Comments'
                            value={checkboxState[manager._id]?.comments || ''}
                            onChange={(e) => handleTextareaChange(manager._id, 'comments', e.target.value)}
                            name={`comments_${manager._id}`}
                            rows={3}
                          />
                        </Col>
                      </Row>
                    )}
                  </div>
                ))}
              </FormGroup>
            </Col>
          </Row>

          {showWarning && <Alert color='danger'>Please select at least one warehouse manager.</Alert>}

          <Row>
            <Col>
              <div className='text-end'>
                <button className='btn btn-success save-user' id='submit-form'>
                  Send Order
                </button>
              </div>
            </Col>
          </Row>
        </AvForm>
      </ModalBody>
    </Modal>
  );
};

export default ModalPriorityUK;
